import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_spectral-design-1/artifacts/cufzcxy8_Futuristic%20rocket-shaped%20logo%20with%20glow%20no%20background.png";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { href: "#features", label: "Features" },
    { href: "#how-it-works", label: "How It Works" },
    { href: "#security", label: "Security" },
    { href: "#pricing", label: "Pricing" },
    { href: "#about", label: "About" },
  ];

  const scrollToWaitlist = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    const element = document.getElementById("cta");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "glass py-2 md:py-3"
            : "bg-transparent py-3 md:py-5"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#"
            className="flex items-center gap-2 md:gap-4 group"
            data-testid="nav-logo"
          >
            <div className="w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl overflow-hidden group-hover:shadow-[0_0_30px_rgba(0,212,255,0.6)] transition-all duration-300">
              <img 
                src={LOGO_URL}
                alt="GHOST"
                className="w-full h-full object-contain"
                style={{
                  filter: "drop-shadow(0 0 12px rgba(0,212,255,0.6))",
                }}
              />
            </div>
            <span className="font-['Unbounded'] font-bold text-xl md:text-2xl lg:text-3xl tracking-tight">
              GHOST
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-sm xl:text-base font-medium text-[#A1A1AA] hover:text-white transition-colors duration-300 relative group"
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] group-hover:w-full transition-all duration-300" />
              </a>
            ))}
          </div>

          {/* Desktop CTA Button */}
          <button
            onClick={scrollToWaitlist}
            className="hidden lg:inline-flex px-6 xl:px-8 py-2.5 xl:py-3 rounded-full bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] text-white font-semibold text-sm xl:text-base hover:shadow-[0_0_30px_rgba(0,212,255,0.5)] transition-all duration-300 ice-hover"
            data-testid="nav-cta-button"
          >
            Join Waitlist
          </button>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-white rounded-lg active:bg-white/10 transition-colors touch-manipulation"
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Mobile Menu Panel */}
            <motion.div
              initial={{ opacity: 0, x: "100%" }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: "100%" }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-[#0a0a14] border-l border-white/10 z-50 lg:hidden overflow-y-auto"
              data-testid="mobile-menu"
            >
              {/* Close button */}
              <div className="flex justify-end p-4">
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-3 text-white rounded-lg active:bg-white/10 transition-colors touch-manipulation"
                  aria-label="Close menu"
                >
                  <X size={28} />
                </button>
              </div>

              {/* Navigation Links */}
              <div className="px-6 py-4 flex flex-col gap-2">
                {navLinks.map((link, index) => (
                  <motion.a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-lg font-medium text-[#A1A1AA] hover:text-white active:text-[#00D4FF] transition-colors py-4 px-4 rounded-xl active:bg-white/5 touch-manipulation"
                  >
                    {link.label}
                  </motion.a>
                ))}
                
                {/* Mobile CTA Button */}
                <motion.button
                  onClick={scrollToWaitlist}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-6 w-full px-6 py-4 rounded-full bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] text-white font-bold text-lg text-center active:scale-95 transition-transform touch-manipulation"
                >
                  Join Waitlist
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
